import { useMemo, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import {
  ArrowUpCircle, Search, ChevronLeft, FileEdit, Loader2, Users,
  FileSpreadsheet, Printer, Hash, ImageIcon,
} from "lucide-react";
import {
  useAdminGradeUpgradesQuery,
  useAdminResetUpgradeToDraftMutation,
} from "../api/upgradeApi";
import {
  STATUS_TABS,
  chapterState,
  countsByTab,
  fmtDate,
  isNotRecommended,
  isPaymentUnsettled,
  isRecommendedPending,
  isFullyEndorsed,
  matchesQuery,
  matchesStatusTab,
  naira,
  refereeCell,
  statusOf,
  tabLabel,
  ENDORSE_LABELS,
  type EndorseState,
  type StatusKey,
} from "../utils/upgradeStatus";
import { exportUpgradesPdf, exportUpgradesSpreadsheet } from "../utils/exportUpgrades";
import IssueMemberNumbersModal from "../components/IssueMemberNumbersModal";
import ExportApplicantPhotosModal from "../components/ExportApplicantPhotosModal";
import ApplicantPhotoLinks from "../components/ApplicantPhotoLinks";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";

function statusColors(key: StatusKey | string) {
  switch (key) {
    case "approved":
      return { border: "border-green-200", active: "bg-green-600 text-white border-green-600", inactive: "bg-green-50 text-green-800 hover:bg-green-100", pill: "bg-green-100 text-green-800 border-green-200" };
    case "under_review":
      return { border: "border-blue-200", active: "bg-blue-600 text-white border-blue-600", inactive: "bg-blue-50 text-blue-800 hover:bg-blue-100", pill: "bg-blue-100 text-blue-800 border-blue-200" };
    case "recommended":
      return { border: "border-emerald-200", active: "bg-emerald-600 text-white border-emerald-600", inactive: "bg-emerald-50 text-emerald-800 hover:bg-emerald-100", pill: "bg-emerald-100 text-emerald-800 border-emerald-200" };
    case "not_recommended":
      return { border: "border-orange-200", active: "bg-orange-600 text-white border-orange-600", inactive: "bg-orange-50 text-orange-800 hover:bg-orange-100", pill: "bg-orange-100 text-orange-800 border-orange-200" };
    case "fully_endorsed":
      return { border: "border-teal-200", active: "bg-teal-600 text-white border-teal-600", inactive: "bg-teal-50 text-teal-800 hover:bg-teal-100", pill: "bg-teal-100 text-teal-800 border-teal-200" };
    case "awaiting_endorsements":
      return { border: "border-violet-200", active: "bg-violet-600 text-white border-violet-600", inactive: "bg-violet-50 text-violet-800 hover:bg-violet-100", pill: "bg-violet-100 text-violet-800 border-violet-200" };
    case "rejected":
      return { border: "border-red-200", active: "bg-red-600 text-white border-red-600", inactive: "bg-red-50 text-red-800 hover:bg-red-100", pill: "bg-red-100 text-red-800 border-red-200" };
    case "returned":
      return { border: "border-orange-200", active: "bg-orange-500 text-white border-orange-500", inactive: "bg-orange-50 text-orange-800 hover:bg-orange-100", pill: "bg-orange-100 text-orange-800 border-orange-200" };
    case "submitted":
      return { border: "border-sky-200", active: "bg-sky-600 text-white border-sky-600", inactive: "bg-sky-50 text-sky-800 hover:bg-sky-100", pill: "bg-sky-100 text-sky-800 border-sky-200" };
    case "pending_payment":
      return { border: "border-yellow-200", active: "bg-yellow-600 text-white border-yellow-600", inactive: "bg-yellow-50 text-yellow-900 hover:bg-yellow-100", pill: "bg-yellow-100 text-yellow-900 border-yellow-200" };
    case "payment_unsettled":
      return { border: "border-rose-200", active: "bg-rose-600 text-white border-rose-600", inactive: "bg-rose-50 text-rose-800 hover:bg-rose-100", pill: "bg-rose-100 text-rose-800 border-rose-200" };
    case "draft":
      return { border: "border-gray-300", active: "bg-gray-600 text-white border-gray-600", inactive: "bg-gray-50 text-gray-700 hover:bg-gray-100", pill: "bg-gray-100 text-gray-700 border-gray-300" };
    default:
      return { border: "border-black", active: "bg-black text-white border-black", inactive: "bg-white text-gray-800 hover:bg-gray-50", pill: "bg-gray-100 text-gray-800 border-gray-200" };
  }
}

function StatusBadge({ status }: { status?: string }) {
  const s = String(status ?? "submitted").toLowerCase();
  const c = statusColors(s);
  return (
    <span className={`inline-flex items-center px-2 py-1 text-xs border rounded-full ${c.pill}`} title={s}>
      {s.replace("_", " ")}
    </span>
  );
}

/* ── Endorsements ──────────────────────────────────────────────────────────
   The chapter and two referees each have to respond before an application can
   move. Shown on the list so the secretariat can see who to chase without
   opening every application. The states themselves are read in
   ../utils/upgradeStatus, which the exports share. */

const ENDORSE_STYLES: Record<EndorseState, string> = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-100",
  rejected: "bg-rose-50 text-rose-700 border-rose-100",
  pending:  "bg-amber-50 text-amber-700 border-amber-100",
  missing:  "bg-gray-50 text-gray-400 border-gray-100",
};

function EndorsementCell({
  name, state, on,
}: {
  name?: string | null;
  state: EndorseState;
  on?: string | null;
}) {
  return (
    <div className="min-w-[9rem]">
      <div className="text-gray-800 truncate" title={name ?? undefined}>
        {name || <span className="text-gray-300">—</span>}
      </div>
      <span
        className={`mt-0.5 inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border rounded-full ${ENDORSE_STYLES[state]}`}
        title={on ? `on ${fmtDate(on)}` : undefined}
      >
        {ENDORSE_LABELS[state]}
      </span>
    </div>
  );
}

function CountPill({ tone, count }: { tone: StatusKey; count: number }) {
  const c = statusColors(tone);
  return <span className={`ml-2 inline-flex items-center px-2 py-0.5 text-xs border rounded-full ${c.pill}`}>{count}</span>;
}

function SummaryCard({ label, value, tone }: { label: string; value: number; tone: StatusKey }) {
  const c = statusColors(tone);
  return (
    <div className={`border rounded-xl p-3 ${c.pill}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs font-medium opacity-80">{label}</div>
    </div>
  );
}

export default function AdminUpgradeCategoryPage() {
  const { categoryId = "" } = useParams();
  const location = useLocation();
  const categoryName = (location.state as any)?.categoryName as string | undefined;

  const [statusTab, setStatusTab] = useState<StatusKey>("all");
  const [q, setQ] = useState("");

  const { data, isLoading, isFetching, isError, refetch } = useAdminGradeUpgradesQuery({
    to_category_id: categoryId,
    per_page: 500,
  });

  const rows: any[] = useMemo(
    () => (Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []),
    [data]
  );

  const title = categoryName ?? rows[0]?.to_category?.name ?? "Category";

  /* Chapter accepted, both referees approved. The set two of the buttons below
     work from — numbering and the photo bundle — so it is derived once. */
  const fullyEndorsed = useMemo(() => rows.filter(isFullyEndorsed), [rows]);

  /* ── Numbering ───────────────────────────────────────────────────────────
     Two grades carry a numbered series, and they are DIFFERENT series in
     different shapes written to different columns:

       Probationer → the P series → members.prob_member_no
       Member      → the Member series → members.member_member_no

     So the button is offered on those two category pages only. "Issue numbers"
     on the Fellow list would be a series that does not exist, and issuing from
     the wrong page cannot be undone. The shapes themselves are portal settings
     (/admin/settings), not anything this screen decides. */
  const gradeKey = String(title).trim().toLowerCase();
  const numberColumn =
    gradeKey === "member" ? "member_member_no" : gradeKey === "probationer" ? "prob_member_no" : null;

  const [showIssueNumbers, setShowIssueNumbers] = useState(false);

  // Waiting on a number: fully endorsed, nothing issued yet IN THIS SERIES.
  // Counted from the rows already on screen so the button can say what it will
  // do before it is pressed; the server decides for real when it runs.
  const awaitingNumbers = useMemo(
    () =>
      numberColumn
        ? fullyEndorsed.filter((r) => !r.user?.member?.[numberColumn]).length
        : 0,
    [fullyEndorsed, numberColumn],
  );

  /* ── Applicant photographs ───────────────────────────────────────────────
     The induction paperwork is built from faces, and they were being collected
     by opening each application one at a time. Taken from the whole category's
     fully endorsed set rather than from the open tab: what is wanted is the
     people who have cleared every endorsement, which is a property of the
     application and not of whichever panel happens to be showing. */
  const [showPhotoExport, setShowPhotoExport] = useState(false);

  const [resetToDraft] = useAdminResetUpgradeToDraftMutation();
  const [resettingId, setResettingId] = useState<string | number | null>(null);

  const doResetToDraft = async (id: string | number) => {
    if (
      !window.confirm(
        "Return this application to draft and clear its payment record? The applicant will need to pay again to resubmit."
      )
    )
      return;
    setResettingId(id);
    try {
      await resetToDraft({ id }).unwrap();
      await refetch();
    } catch (e: any) {
      alert(e?.data?.message || "Could not reset this application.");
    } finally {
      setResettingId(null);
    }
  };

  // Counted where each row is LISTED, not where its status column says —
  // otherwise a tab's pill promises rows the tab does not show.
  const statusCounts = useMemo(() => countsByTab(rows), [rows]);

  const filtered = useMemo(
    () => rows.filter((r) => matchesStatusTab(r, statusTab) && matchesQuery(r, q)),
    [rows, statusTab, q],
  );

  // Exports carry what is on screen — the same rows, filed the same way — plus
  // the whole category's breakdown, so a listing always says what it is a
  // listing of.
  const exportMeta = { category: title, tab: statusTab, query: q };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <Link to="/admin/upgrade-applications" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-3">
        <ChevronLeft className="w-4 h-4" /> All upgrade categories
      </Link>

      <div className="bg-white border rounded-xl p-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
              <ArrowUpCircle className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Upgrades to {title}</h1>
              <p className="text-sm text-gray-500 mt-0.5">Review members applying to become {title}.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Issues the running series of Member numbers to the fully
                endorsed applicants who do not hold one. Always offered on the
                Member page, including at zero — a button that disappears once
                the work is done reads as a missing feature rather than as
                "nobody is waiting", which the dialog says plainly. */}
            {numberColumn && (
              <AccessGate perm={PERMS.upgrade.memberNumberIssue}>
                <button
                  onClick={() => setShowIssueNumbers(true)}
                  disabled={isLoading}
                  title={`Issue ${title} numbers to fully endorsed applicants`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-sm font-semibold hover:bg-indigo-100 disabled:opacity-50"
                >
                  <Hash className="w-4 h-4" />
                  Issue {title.toLowerCase()} numbers
                  {awaitingNumbers > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-indigo-600 text-white text-[11px] font-bold">
                      {awaitingNumbers}
                    </span>
                  )}
                </button>
              </AccessGate>
            )}
            {/* The one export that is NOT the open panel — and says so, both on
                the button and in the dialog. The photographs are wanted for the
                fully endorsed applicants specifically, so tying them to the tab
                would quietly hand over a different set each time. */}
            <button
              onClick={() => setShowPhotoExport(true)}
              disabled={isLoading}
              title={`Download both photographs of the ${fullyEndorsed.length} fully endorsed applicant(s) as a zip`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-violet-200 text-violet-700 text-sm font-semibold hover:bg-violet-50 disabled:opacity-50"
            >
              <ImageIcon className="w-4 h-4" />
              Photos
              {fullyEndorsed.length > 0 && (
                <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-violet-600 text-white text-[11px] font-bold">
                  {fullyEndorsed.length}
                </span>
              )}
            </button>
            {/* Both exports take the panel currently open, named in the file
                and printed on the page — an export of "everything" when you
                asked for the recommended list is worse than no export. */}
            <button
              onClick={() => exportUpgradesSpreadsheet(filtered, rows, exportMeta)}
              disabled={isLoading}
              title={`Download the ${tabLabel(statusTab)} listing as a spreadsheet`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 disabled:opacity-50"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Excel
            </button>
            <button
              onClick={() => exportUpgradesPdf(filtered, rows, exportMeta)}
              disabled={isLoading}
              title={`Print the ${tabLabel(statusTab)} listing, or save it as a PDF`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-rose-200 text-rose-700 text-sm font-semibold hover:bg-rose-50 disabled:opacity-50"
            >
              <Printer className="w-4 h-4" />
              PDF
            </button>
            {/* Assignment happens inside a review cycle, worked from its own
                screen — the same way logbooks are handed to assessors in a diet.
                The grade travels with the link so the cycles screen opens on
                THIS category's work rather than on every grade at once; both the
                id and the name go, because the cycle screens filter by name (the
                only thing their rows carry) and label by it too. */}
            <Link
              to={`/admin/upgrade-applications/review-cycles?category=${encodeURIComponent(
                categoryId,
              )}&grade=${encodeURIComponent(title)}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-200 text-indigo-700 text-sm font-semibold hover:bg-indigo-50"
            >
              <Users className="w-4 h-4" />
              Review cycles
            </Link>
            <button onClick={() => refetch()} disabled={isFetching} className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm">
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-5">
          {STATUS_TABS.map((t) => (
            <SummaryCard key={t.key} label={t.label} value={statusCounts.get(t.key) || 0} tone={t.key} />
          ))}
        </div>

        {/* Search */}
        <div className="mt-5 relative max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by member, membership no, email, route…"
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
          />
        </div>

        {/* Status tabs */}
        <div className="flex flex-wrap gap-2 mt-4">
          {STATUS_TABS.map((t) => {
            const active = statusTab === t.key;
            const c = statusColors(t.key);
            return (
              <button
                key={t.key}
                onClick={() => setStatusTab(t.key)}
                className={["px-3 py-2 text-sm border rounded-md transition", active ? c.active : `${c.border} ${c.inactive}`].join(" ")}
              >
                {t.label}
                <CountPill tone={t.key} count={statusCounts.get(t.key) || 0} />
              </button>
            );
          })}
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="mt-6 text-sm text-gray-500">Loading applications…</div>
        ) : isError ? (
          <div className="mt-6 text-sm text-red-600">Failed to load applications.</div>
        ) : filtered.length === 0 ? (
          <div className="mt-6 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
            No upgrade applications match your filters.
          </div>
        ) : (
          <div className="mt-5 border rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="px-4 py-2 font-medium">Member</th>
                  {/* The issued number, so the result of the button is visible
                      on the list it was pressed from rather than only in the
                      export. */}
                  {numberColumn && <th className="px-4 py-2 font-medium">New No.</th>}
                  <th className="px-4 py-2 font-medium">From</th>
                  <th className="px-4 py-2 font-medium">Route</th>
                  <th className="px-4 py-2 font-medium">Fee</th>
                  <th className="px-4 py-2 font-medium">Chapter</th>
                  <th className="px-4 py-2 font-medium">Referee 1</th>
                  <th className="px-4 py-2 font-medium">Referee 2</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  {/* Offered on fully endorsed rows only — the same set the
                      bundle export takes, and the only one anyone is building
                      induction paperwork from. */}
                  <th className="px-4 py-2 font-medium">Photos</th>
                  <th className="px-4 py-2 font-medium">Submitted</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.user?.name ?? "—"}</div>
                      <div className="text-xs text-gray-400">{r.user?.member?.membership_no ?? r.user?.email ?? ""}</div>
                    </td>
                    {numberColumn && (
                      <td className="px-4 py-3">
                        {r.user?.member?.[numberColumn] ? (
                          <span className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 font-mono font-bold text-xs">
                            {r.user.member[numberColumn]}
                          </span>
                        ) : isFullyEndorsed(r) ? (
                          <span className="text-[11px] text-amber-600 font-semibold">Awaiting</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-gray-700">{r.from_category?.name ?? "—"}</td>
                    <td className="px-4 py-3">
                      {r.route ? (
                        <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-semibold uppercase">{r.route}</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{naira(r.amount != null ? Number(r.amount) / 100 : null)}</td>

                    {/* Who still owes an endorsement — the reason this list is
                        opened most days. */}
                    <td className="px-4 py-3">
                      <EndorsementCell
                        name={r.chapter?.name ?? r.chapter_other}
                        state={chapterState(r)}
                        on={r.chapter_accepted_at}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <EndorsementCell {...refereeCell(r, 0)} />
                    </td>
                    <td className="px-4 py-3">
                      <EndorsementCell {...refereeCell(r, 1)} />
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <StatusBadge status={r.status} />
                        {/* Listed under Approved on the committee's
                            recommendation — the chairman has not finalised it,
                            so the real status is still shown above. */}
                        {isRecommendedPending(r) && statusOf(r) !== "recommended" && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 text-[11px] border rounded-full bg-green-100 text-green-800 border-green-200"
                            title={`Recommended by ${r.committee_approved_count} committee member(s)`}
                          >
                            Recommended ({r.committee_approved_count})
                          </span>
                        )}
                        {statusOf(r) === "recommended" && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 text-[11px] border rounded-full bg-amber-100 text-amber-800 border-amber-200"
                            title="Recommended by the committee — the applicant's induction fees are outstanding"
                          >
                            Induction fees due
                          </span>
                        )}
                        {isNotRecommended(r) && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 text-[11px] border rounded-full bg-orange-100 text-orange-800 border-orange-200"
                            title="The committee recommended against this — awaiting the chairman's final decision"
                          >
                            Awaiting chairman
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {isFullyEndorsed(r) ? (
                        <ApplicantPhotoLinks row={r} />
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {isPaymentUnsettled(r) && (
                          <button
                            onClick={() => doResetToDraft(r.id)}
                            disabled={resettingId === r.id}
                            title="Return to draft and clear the unsettled payment"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-200 text-rose-700 text-xs font-semibold hover:bg-rose-50 disabled:opacity-60"
                          >
                            {resettingId === r.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <FileEdit className="w-3.5 h-3.5" />
                            )}
                            Reset to draft
                          </button>
                        )}
                        <Link to={`/admin/upgrade-applications/${r.id}`} className="px-3 py-1.5 rounded-lg border text-xs hover:bg-gray-50">
                          Review
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showPhotoExport && (
        <ExportApplicantPhotosModal
          rows={fullyEndorsed}
          categoryName={title}
          onClose={() => setShowPhotoExport(false)}
        />
      )}

      {showIssueNumbers && (
        <IssueMemberNumbersModal
          categoryId={categoryId}
          categoryName={title}
          onClose={() => {
            setShowIssueNumbers(false);
            // The list carries each member's number, so it has to be re-read —
            // otherwise the table still shows "Awaiting" for people who now
            // hold a number.
            refetch();
          }}
        />
      )}
    </div>
  );
}
